import React from 'react'
import { Box, Button, Tooltip, Typography, Card, Grid, LinearProgress, Stack, Chip, useTheme, useMediaQuery } from '@mui/material'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import AddIcon from '@mui/icons-material/Add'
import { AIQuotaBucket } from '../../services/api'
import { formatDateDDMMYYYY } from '../../utils/dateUtils'

export interface AICreditBalanceCardProps {
  hasGrowthSubscription: boolean
  quotaBuckets: AIQuotaBucket[]
  onBuyTopUpClick: () => void
}

export const AICreditBalanceCard: React.FC<AICreditBalanceCardProps> = ({
  hasGrowthSubscription,
  quotaBuckets,
  onBuyTopUpClick
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  if (quotaBuckets.length === 0) {
    return null
  }

  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: '#e2e8f0', borderRadius: 4, p: { xs: 2.5, sm: 3.5 }, mb: 4, bgcolor: '#ffffff' }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, mb: 3 }}>
        <Box>
          <Stack direction={isMobile ? 'column' : 'row'} spacing={1} alignItems={isMobile ? 'flex-start' : 'center'}>
            <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={800} color="#0f172a" sx={{ letterSpacing: '-0.02em' }}>
              AI Reply Credits
            </Typography>
            {!hasGrowthSubscription && (
              <Chip
                icon={<LockOutlinedIcon sx={{ fontSize: '13px !important' }} />}
                label="Growth Plan Required"
                size="small"
                color="warning"
                sx={{ fontWeight: 700, fontSize: '0.65rem', height: 20 }}
              />
            )}
          </Stack>
          <Typography variant="caption" color="#64748b" sx={{ display: 'block', mt: 0.5, fontSize: isMobile ? '0.775rem' : '0.875rem', lineHeight: 1.4 }}>
            Track your credit allowance and validity
          </Typography>
        </Box>
        
        <Tooltip title={!hasGrowthSubscription ? "Growth Plan required to purchase top-up credits" : ""}>
          <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={onBuyTopUpClick}
              disabled={!hasGrowthSubscription}
              startIcon={<AddIcon />}
              sx={{ borderRadius: 2.5, fontWeight: 800, textTransform: 'none', px: 2, py: 0.5, fontSize: '0.875rem', width: { xs: '100%', sm: 'auto' } }}
            >
              Top-Up Credits
            </Button>
          </Box>
        </Tooltip>
       
      </Box>

      {!hasGrowthSubscription && (
        <Box
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2.5,
            bgcolor: '#fffbeb',
            border: '1px solid #fef3c7',
            borderLeft: '4px solid #f59e0b',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Box sx={{ color: '#d97706', display: 'flex', flexShrink: 0 }}>
            <LockOutlinedIcon sx={{ fontSize: 18 }} />
          </Box>
          <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#92400e' }}>
            AI top-up credits remain saved in your account, but require an active Growth Plan to send automated replies.
          </Typography>
        </Box>
      )}

      <Stack spacing={2}>
        {quotaBuckets.map((bucket) => {
          const percentage = bucket.remaining_units === bucket.total_units
            ? 100
            : Math.floor((bucket.remaining_units / Math.max(1, bucket.total_units)) * 1000) / 10

          return (
            <Box
              key={bucket.id}
              sx={{
                p: { xs: 1.75, sm: 2.5 },
                bgcolor: '#f8fafc',
                border: '1px solid #f1f5f9',
                borderRadius: 2.5,
              }}
            >
              <Grid container spacing={isMobile ? 1 : 2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" fontWeight={700} color="#0f172a" sx={{ fontSize: isMobile ? '0.875rem' : '0.95rem' }}>
                    {bucket.bucket_type === 'topup'
                      ? 'Top-Up Credits'
                      : 'Monthly Plan Credits'}
                  </Typography>
                  <Typography variant="caption" color="#64748b" sx={{ fontSize: isMobile ? '0.725rem' : '0.8rem', display: 'block', mt: 0.25 }}>
                    Valid until: {formatDateDDMMYYYY(bucket.expires_at)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                    <Typography variant="caption" fontWeight={700} color="#0f172a" sx={{ fontSize: isMobile ? '0.75rem' : '0.825rem' }}>
                      {bucket.remaining_units.toLocaleString()} / {bucket.total_units.toLocaleString()} AI replies left
                    </Typography>
                    <Typography variant="caption" fontWeight={700} color="#2563eb" sx={{ fontSize: isMobile ? '0.7rem' : '0.775rem' }}>
                      {percentage}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={percentage}
                    sx={{
                      height: isMobile ? 6 : 8,
                      borderRadius: 4,
                      bgcolor: '#e2e8f0',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                        background: 'linear-gradient(90deg, #2563eb 0%, #3b82f6 100%)',
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          )
        })}
      </Stack>
    </Card>
  )
}
